export type ShotPurpose = "approach" | "layup";
export type PinLateralThird = "left" | "center" | "right";
export type PinDepthThird = "front" | "middle" | "back";

export type HazardSeverity = "none" | "near" | "adjacent";

export type TargetingInputs = {
  shotPurpose?: ShotPurpose;
  shotDistanceYds: number;

  pinLateralThird: PinLateralThird;
  pinDepthThird: PinDepthThird;

  leftPenalty: HazardSeverity;
  rightPenalty: HazardSeverity;
  frontPenalty: HazardSeverity;
  backPenalty: HazardSeverity;

  leftBunker: HazardSeverity;
  rightBunker: HazardSeverity;
  frontBunker: HazardSeverity;
  backBunker: HazardSeverity;

  windBucket?: string;
  slopeYds?: number;
  confidenceScore?: number;
};

export type TargetRecommendation = {
  targetLateralYds: number; // + right of pin, - left of pin
  targetDepthYds: number;   // + past pin, - short of pin
  targetZone: string;
  rationale: string[];
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function basePinOffsetYds(shotDistanceYds: number): number {
  // Your first rule:
  // yardage / 2 * 10% = yardage * 0.05
  const raw = shotDistanceYds * 0.05;
  return clamp(Math.round(raw * 10) / 10, 1, 15);
}

function hazardPenaltyYds(severity: HazardSeverity): number {
  if (severity === "adjacent") return 3;
  if (severity === "near") return 1.5;
  return 0;
}
function pinDepthAdjustmentYds(
  shotDistanceYds: number,
  pinDepthThird: PinDepthThird
): number {
  if (pinDepthThird === "front") {
    return Math.min(shotDistanceYds * 0.05, 10);
  }
  if (pinDepthThird === "back") {
    return -Math.min(shotDistanceYds * 0.025, 5);
  }
  return 0;
}
export function computeTargetRecommendation(
  inputs: TargetingInputs
): TargetRecommendation {
const {
  shotDistanceYds,
  shotPurpose = "approach",
  pinLateralThird,
  pinDepthThird,
  leftPenalty,
  rightPenalty,
  frontPenalty,
  backPenalty,
  leftBunker,
  rightBunker,
  frontBunker,
  backBunker,
} = inputs;

  const rationale: string[] = [];
  const baseOffset = basePinOffsetYds(shotDistanceYds);
  const depthAdj = pinDepthAdjustmentYds(shotDistanceYds, pinDepthThird);
  let targetLateralYds = 0;
  let targetDepthYds = 0;

  // --- lateral target logic ---
  // Penalty areas matter more than bunkers because hazardPenaltyYds already
  // weights severity by side.
  const leftRisk = hazardPenaltyYds(leftPenalty) + hazardPenaltyYds(leftBunker);
  const rightRisk = hazardPenaltyYds(rightPenalty) + hazardPenaltyYds(rightBunker);

  if (pinLateralThird === "left") {
    targetLateralYds += baseOffset;
    rationale.push("Left-side pin shifts baseline landing target to the right.");
  } else if (pinLateralThird === "right") {
    targetLateralYds -= baseOffset;
    rationale.push("Right-side pin shifts baseline landing target to the left.");
  } else {
    // Center pin: favor the safer side when side risk is asymmetric.
    if (leftRisk > rightRisk) {
      targetLateralYds += baseOffset;
      rationale.push("Center pin favors the safer right side because left-side trouble is greater.");
    } else if (rightRisk > leftRisk) {
      targetLateralYds -= baseOffset;
      rationale.push("Center pin favors the safer left side because right-side trouble is greater.");
    }
  }

  if (leftRisk > 0) {
    targetLateralYds += leftRisk;
    rationale.push("Left-side trouble pushes landing target farther right.");
  }

  if (rightRisk > 0) {
    targetLateralYds -= rightRisk;
    rationale.push("Right-side trouble pushes landing target farther left.");
  }

  // --- depth target logic ---
  // --- depth target logic ---
  if (depthAdj > 0) {
    targetDepthYds += depthAdj;
    rationale.push("Front pin increases landing depth target.");
  } else if (depthAdj < 0) {
    targetDepthYds += depthAdj;
    rationale.push("Back pin decreases landing depth target.");
  }

  const frontRisk = hazardPenaltyYds(frontPenalty) + hazardPenaltyYds(frontBunker);
  const backRisk = hazardPenaltyYds(backPenalty) + hazardPenaltyYds(backBunker);

  if (frontRisk > 0) {
    targetDepthYds += frontRisk;
    rationale.push("Front trouble pushes target deeper.");
  }

  if (backRisk > 0) {
    targetDepthYds -= backRisk;
    rationale.push("Back trouble pushes target shorter.");
  }

  const targetZoneParts: string[] = [];

  if (targetDepthYds > 2) targetZoneParts.push("back");
  else if (targetDepthYds < -2) targetZoneParts.push("front");
  else targetZoneParts.push("middle");

  if (targetLateralYds > 2) targetZoneParts.push("right");
  else if (targetLateralYds < -2) targetZoneParts.push("left");
  else targetZoneParts.push("center");

  const targetZone = `${targetZoneParts[0]}-${targetZoneParts[1]}`;

  return {
    targetLateralYds: Math.round(targetLateralYds * 10) / 10,
    targetDepthYds: Math.round(targetDepthYds * 10) / 10,
    targetZone,
    rationale,
  };
}
const example = computeTargetRecommendation({
  shotDistanceYds: 180,
  pinLateralThird: "left",
  pinDepthThird: "middle",

  leftPenalty: "adjacent",
  rightPenalty: "none",
  frontPenalty: "none",
  backPenalty: "none",

  leftBunker: "none",
  rightBunker: "none",
  frontBunker: "none",
  backBunker: "none",
});

console.log("[SS][TARGETING] example", example);